import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { lobService } from '../../services/api/lob.service';

// Async thunks
export const fetchAllLOBs = createAsyncThunk(
  'lob/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const response = await lobService.getAllLOBs(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchLOBById = createAsyncThunk(
  'lob/fetchById',
  async (lobId, { rejectWithValue }) => {
    try {
      const response = await lobService.getLOBById(lobId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createLOB = createAsyncThunk(
  'lob/create',
  async (lobData, { rejectWithValue }) => {
    try {
      const response = await lobService.createLOB(lobData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateLOB = createAsyncThunk(
  'lob/update',
  async ({ lobId, lobData }, { rejectWithValue }) => {
    try {
      const response = await lobService.updateLOB(lobId, lobData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteLOB = createAsyncThunk(
  'lob/delete',
  async (lobId, { rejectWithValue }) => {
    try {
      const response = await lobService.deleteLOB(lobId);
      return { lobId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const activateLOB = createAsyncThunk(
  'lob/activate',
  async (lobId, { rejectWithValue }) => {
    try {
      const response = await lobService.activateLOB(lobId);
      return { lobId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deactivateLOB = createAsyncThunk(
  'lob/deactivate',
  async (lobId, { rejectWithValue }) => {
    try {
      const response = await lobService.deactivateLOB(lobId);
      return { lobId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  lobs: [],
  currentLOB: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  },
  filters: {
    search: '',
    sortBy: 'display_order',
    sortOrder: 'asc',
  },
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
  successMessage: null,
};

const lobSlice = createSlice({
  name: 'lob',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearCurrentLOB: (state) => {
      state.currentLOB = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    resetLOBState: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // Fetch all LOBs
    builder
      .addCase(fetchAllLOBs.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllLOBs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.lobs = action.payload.data || [];
        if (action.payload.pagination) {
          state.pagination = {
            currentPage: action.payload.pagination.currentPage,
            totalPages: action.payload.pagination.totalPages,
            totalItems: action.payload.pagination.totalItems,
            itemsPerPage: action.payload.pagination.itemsPerPage,
          };
        }
      })
      .addCase(fetchAllLOBs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to fetch LOBs';
      });

    // Fetch LOB by ID
    builder
      .addCase(fetchLOBById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLOBById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentLOB = action.payload.data;
      })
      .addCase(fetchLOBById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to fetch LOB';
      });

    // Create LOB
    builder
      .addCase(createLOB.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createLOB.fulfilled, (state, action) => {
        state.isCreating = false;
        state.lobs.push(action.payload.data);
        state.successMessage = action.payload.message || 'LOB created successfully';
      })
      .addCase(createLOB.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload?.message || 'Failed to create LOB';
      });

    // Update LOB
    builder
      .addCase(updateLOB.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateLOB.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.lobs.findIndex(
          (lob) => lob.lob_id === action.payload.data.lob_id
        );
        if (index !== -1) {
          state.lobs[index] = action.payload.data;
        }
        state.currentLOB = action.payload.data;
        state.successMessage = action.payload.message || 'LOB updated successfully';
      })
      .addCase(updateLOB.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload?.message || 'Failed to update LOB';
      });

    // Delete LOB
    builder
      .addCase(deleteLOB.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteLOB.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.lobs = state.lobs.filter((lob) => lob.lob_id !== action.payload.lobId);
        state.successMessage = action.payload.message || 'LOB deleted successfully';
      })
      .addCase(deleteLOB.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload?.message || 'Failed to delete LOB';
      });

    // Activate LOB
    builder
      .addCase(activateLOB.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(activateLOB.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.lobs.findIndex((lob) => lob.lob_id === action.payload.lobId);
        if (index !== -1) {
          state.lobs[index].is_active = true;
        }
        state.successMessage = action.payload.message || 'LOB activated successfully';
      })
      .addCase(activateLOB.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload?.message || 'Failed to activate LOB';
      });

    // Deactivate LOB
    builder
      .addCase(deactivateLOB.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(deactivateLOB.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.lobs.findIndex((lob) => lob.lob_id === action.payload.lobId);
        if (index !== -1) {
          state.lobs[index].is_active = false;
        }
        state.successMessage = action.payload.message || 'LOB deactivated successfully';
      })
      .addCase(deactivateLOB.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload?.message || 'Failed to deactivate LOB';
      });
  },
});

export const {
  setFilters,
  clearCurrentLOB,
  clearError,
  clearSuccessMessage,
  resetLOBState,
} = lobSlice.actions;

export default lobSlice.reducer;
